import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";

import type { NavigationSectionGroup } from "@/components/Navigation/NavigationCard";
import { CForm } from "@/features/c-form/CForm";
import useZodValidation from "@/hooks/useZodValidation";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { queryClient } from "@/lib/queryClient";

import {
  useSoftwareFromAppIntegration,
  useUpdateSoftwareArchitectureMutator,
} from "../-query";
import { appIntegrationKey } from "../-query/key";

import { ExecutionRequirementsSection } from "./-components/ExecutionRequirementsSection";
import { SecurityComplianceSection } from "./-components/SecurityComplianceSection";
import { SoftwareComponentSection } from "./-components/SoftwareComponentSection";
import { SoftwareDependencySection } from "./-components/SoftwareDependencySection";
import { SoftwareInstanceSection } from "./-components/SoftwareInstanceSection";
import { VersionBuildInformationSection } from "./-components/VersionBuildInformationSection";
import {
  AppIntegrationArchitectureStore,
  useAppIntegrationArchitectureStore,
} from "./-store/store";

export const Route = createFileRoute(
  "/_staff/app-integrations/$id/_$id/architecture/",
)({
  component: AppIntegrationArchitecture,
});

function AppIntegrationArchitecture() {
  const store = AppIntegrationArchitectureStore.createStore();
  return (
    <AppIntegrationArchitectureStore.Provider value={store}>
      <Content />
    </AppIntegrationArchitectureStore.Provider>
  );
}

function Content() {
  const { id } = Route.useParams();
  const { data } = useSoftwareFromAppIntegration(id);

  const { values, actions } = useAppIntegrationArchitectureStore();
  useEffect(() => {
    if (data) {
      actions.reset(data);
    }
  }, [actions, data]);

  // Use existing software API for updates
  const updateSoftwareArchitectureMutation =
    useUpdateSoftwareArchitectureMutator(data?.id || id, {
      onSuccess: async () => {
        await queryClient.refetchQueries({
          queryKey: appIntegrationKey.software(id),
        });
      },
    });

  const handleSave = useCallback(async () => {
    if (!actions.validate()) {
      throw new Error("Validation failed");
    }
    const result = await updateSoftwareArchitectureMutation.mutateAsync(values);
    return result;
  }, [actions, updateSoftwareArchitectureMutation, values]);

  const handleReset = useCallback(() => {
    if (data) {
      actions.reset(data);
    }
  }, [actions, data]);

  useZodValidation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.profile.architecture.navigation",
  });

  const navigationSectionGroups: NavigationSectionGroup[] = [
    {
      titleLabel: t("versionBuildInformation"),
      to: "",
      hash: "version-build-information",
    },
    {
      titleLabel: t("softwareComponent"),
      to: "",
      hash: "app-integration-component",
    },
    {
      titleLabel: t("softwareDependency"),
      to: "",
      hash: "app-integration-dependency",
    },
    {
      titleLabel: t("softwareInstance"),
      to: "",
      hash: "app-integration-instance",
    },
    {
      titleLabel: t("executionRequirements"),
      to: "",
      hash: "execution-requirements",
    },
    {
      titleLabel: t("securityCompliance"),
      to: "",
      hash: "security-compliance",
    },
  ];

  return (
    <CForm
      editUnit="section"
      itemLayout="label"
      navigationSectionGroups={navigationSectionGroups}
      onSave={handleSave}
      onReset={handleReset}
    >
      <VersionBuildInformationSection />
      <SoftwareComponentSection />
      <SoftwareDependencySection />
      <SoftwareInstanceSection />
      <ExecutionRequirementsSection />
      <SecurityComplianceSection />
    </CForm>
  );
}
